import React, { useState } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, TextField, Button,
  Alert, FormControlLabel, Checkbox,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Search as SearchIcon } from '@mui/icons-material';
import { CuentasService } from '../services/cuentasService';
import type { CuentaPorCobrarPagar, CuentasFiltro } from '../services/cuentasService';

const CuentasCobrarPagarPage: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [filtro, setFiltro] = useState<CuentasFiltro>({});
  const [data, setData] = useState<CuentaPorCobrarPagar[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConsultar = async () => {
    setLoading(true);
    setError(null);
    try {
      const result =
        tabIndex === 0
          ? await CuentasService.getCuentasPorCobrar(filtro)
          : await CuentasService.getCuentasPorPagar(filtro);
      setData(result);
    } catch {
      setError('Error al consultar cuentas');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_: unknown, newValue: number) => {
    setTabIndex(newValue);
    setData([]);
  };

  const columns: GridColDef[] = [
    { field: 'tipoDocumento', headerName: 'Tipo Doc.', width: 90 },
    { field: 'serieDocumento', headerName: 'Serie', width: 80 },
    { field: 'numeroDocumento', headerName: 'Nro. Doc.', width: 120 },
    {
      field: 'fechaEmision',
      headerName: 'F. Emision',
      width: 110,
      valueFormatter: (value: string) =>
        value ? new Date(value).toLocaleDateString('es-PE') : '',
    },
    {
      field: 'fechaVencimiento',
      headerName: 'F. Vencimiento',
      width: 120,
      valueFormatter: (value: string) =>
        value ? new Date(value).toLocaleDateString('es-PE') : '',
    },
    {
      field: 'nombreAuxiliar',
      headerName: tabIndex === 0 ? 'Cliente' : 'Proveedor',
      flex: 1,
      minWidth: 200,
    },
    { field: 'numeroIdentificacion', headerName: 'RUC/DNI', width: 120 },
    {
      field: 'importeTotal',
      headerName: 'Importe',
      width: 130,
      type: 'number',
      valueFormatter: (value: number) =>
        value?.toLocaleString('es-PE', { minimumFractionDigits: 2 }),
    },
    {
      field: 'pagosACuenta',
      headerName: 'Pagos',
      width: 130,
      type: 'number',
      valueFormatter: (value: number) =>
        value?.toLocaleString('es-PE', { minimumFractionDigits: 2 }),
    },
    {
      field: 'saldo',
      headerName: 'Saldo',
      width: 130,
      type: 'number',
      valueFormatter: (value: number) =>
        value?.toLocaleString('es-PE', { minimumFractionDigits: 2 }),
    },
    { field: 'monedaSimbolo', headerName: 'Mon.', width: 60 },
    { field: 'diasVencido', headerName: 'Dias', width: 70, type: 'number' },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
        Cuentas por Cobrar / Pagar
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabIndex} onChange={handleTabChange}>
          <Tab label="Cuentas por Cobrar" />
          <Tab label="Cuentas por Pagar" />
        </Tabs>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Fecha Inicio"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filtro.fechaInicio || ''}
            onChange={(e) => setFiltro({ ...filtro, fechaInicio: e.target.value })}
          />
          <TextField
            label="Fecha Fin"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={filtro.fechaFin || ''}
            onChange={(e) => setFiltro({ ...filtro, fechaFin: e.target.value })}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filtro.mostrarSaldados || false}
                onChange={(e) =>
                  setFiltro({ ...filtro, mostrarSaldados: e.target.checked })
                }
              />
            }
            label="Mostrar saldados"
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleConsultar}
            disabled={loading}
          >
            Consultar
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ height: 'calc(100vh - 320px)' }}>
        <DataGrid
          rows={data}
          columns={columns}
          getRowId={(row) => row.idMovimiento}
          loading={loading}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
        />
      </Paper>
    </Box>
  );
};

export default CuentasCobrarPagarPage;
